import { countries } from '@/data/countries';
import localeData from '@/data/locale.json';

type LanguageCode = keyof (typeof localeData)['primary-dialects'];

export interface SetupPreviewInitContext {
  storeId: string;
  funnelId: string;
  currency: string;
  checkoutUrl?: string | null;
  lineItem: { variantId: string; priceId: string; quantity: number } | null;
  i18nLanguage?: string;
  shippingSnapshot?: { previewCountryCode?: string | null } | null;
  shippingRates?: Array<{ countryCodes?: string[] | null }>;
}

/** Map CRM i18n language (en, fr) to checkout BCP-47 locale. */
export function resolveSetupCheckoutLocale(language?: string): string {
  const normalized = (language ?? 'en').split('-')[0] as LanguageCode;
  return localeData['primary-dialects'][normalized] ?? localeData['primary-dialects'].en;
}

/**
 * Checkout sessions expect ISO alpha-2 (FR, US).
 * Shipping rates may store numeric ISO 3166-1 codes (250, 004) — convert when needed.
 */
export function normalizeSetupCountryCode(code: string | undefined | null): string | null {
  if (!code?.trim()) return null;

  const normalized = code.trim().toUpperCase();

  if (/^[A-Z]{2}$/.test(normalized)) {
    return normalized;
  }

  const numericCandidates = [normalized, normalized.padStart(3, '0')];
  for (const numeric of numericCandidates) {
    const byNumeric = countries.find((country) => country['country-code'] === numeric);
    if (byNumeric) {
      return byNumeric['alpha-2'];
    }
  }

  if (/^[A-Z]{3}$/.test(normalized)) {
    const byAlpha3 = countries.find((country) => country['alpha-3'] === normalized);
    if (byAlpha3) {
      return byAlpha3['alpha-2'];
    }
  }

  return null;
}

/** Derive a default shipping country from a BCP-47 locale (fr-FR → FR). */
export function resolveSetupCountryFromLocale(locale: string): string {
  const bcp47 = locale.includes('-') ? locale : resolveSetupCheckoutLocale(locale);
  const region = bcp47.split('-')[1];
  return (region ?? 'US').toUpperCase();
}

/** Pick a country compatible with configured shipping rates, or fall back to locale. */
export function resolveSetupShippingCountryCode(
  rates: Array<{ countryCodes?: string[] | null }> | undefined,
  locale: string,
): string {
  for (const rate of rates ?? []) {
    for (const rawCode of rate.countryCodes ?? []) {
      const alpha2 = normalizeSetupCountryCode(rawCode);
      if (alpha2) return alpha2;
    }
  }

  return resolveSetupCountryFromLocale(locale);
}

export function resolveSetupPreviewInitParams(params: SetupPreviewInitContext): {
  locale: string;
  shippingCountryCode: string;
} {
  const locale = resolveSetupCheckoutLocale(params.i18nLanguage);
  const shippingCountryCode =
    normalizeSetupCountryCode(params.shippingSnapshot?.previewCountryCode) ??
    resolveSetupShippingCountryCode(params.shippingRates, locale);

  return { locale, shippingCountryCode };
}
